/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { createContext, useContext } from 'react';
import {
  Accordion as ShadcnAccordion,
  AccordionItem as ShadcnAccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/shared/ui/components/shadcn/accordion';
import { cn } from '@/shared/ui/utils/tailwind';

type AccordionClassNames = {
  item?: string;
  control?: string;
  label?: string;
  content?: string;
  panel?: string;
  chevron?: string;
};

const AccordionClassNamesContext = createContext<AccordionClassNames>({});
const AccordionRadiusContext = createContext<string | undefined>(undefined);

export type AccordionItemProps = {
  value: string;
  children: React.ReactNode;
  className?: string;
};

export type AccordionControlProps = {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
};

export type AccordionPanelProps = {
  children: React.ReactNode;
  className?: string;
};

export type AccordionProps = {
  children: React.ReactNode;
  defaultValue?: string | string[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  multiple?: boolean;
  variant?: 'default' | 'contained' | 'separated';
  radius?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  classNames?: {
    item?: string;
    control?: string;
    label?: string;
    content?: string;
    panel?: string;
    chevron?: string;
  };
};

/**
 * Accordion adapter that delegates to the shadcn Accordion (Radix primitive).
 *
 * @param {AccordionProps} props - Accordion properties.
 * @returns {JSX.Element} Rendered accordion.
 */
export const Accordion: React.FC<AccordionProps> & {
  Item: typeof AccordionItem;
  Control: typeof AccordionControl;
  Panel: typeof AccordionPanel;
} = ({
  children,
  defaultValue,
  value,
  onChange,
  multiple = false,
  variant = 'default',
  radius,
  className,
  classNames,
}) => {
  const variantClass = variant === 'separated' ? 'space-y-2' : '';

  const radiusMap: Record<string, string> = {
    xs: '2px', sm: '4px', md: '8px', lg: '16px', xl: '32px',
  };
  const radiusValue = radius != null
    ? typeof radius === 'number' ? `${radius}px` : (radiusMap[radius] || radius)
    : undefined;

  const inner = (
    <AccordionClassNamesContext.Provider value={classNames || {}}>
      <AccordionRadiusContext.Provider value={radiusValue}>
        {children}
      </AccordionRadiusContext.Provider>
    </AccordionClassNamesContext.Provider>
  );

  if (multiple) {
    return (
      <ShadcnAccordion
        type="multiple"
        defaultValue={Array.isArray(defaultValue) ? defaultValue : defaultValue ? [defaultValue] : undefined}
        value={Array.isArray(value) ? value : undefined}
        onValueChange={onChange as (value: string[]) => void}
        className={cn(variantClass, className)}
      >
        {inner}
      </ShadcnAccordion>
    );
  }

  return (
    <ShadcnAccordion
      type="single"
      collapsible
      defaultValue={typeof defaultValue === 'string' ? defaultValue : undefined}
      value={typeof value === 'string' ? value : undefined}
      onValueChange={onChange as (value: string) => void}
      className={cn(variantClass, className)}
    >
      {inner}
    </ShadcnAccordion>
  );
};

/**
 * Accordion item wrapper.
 *
 * @param {AccordionItemProps} props - Item properties.
 * @returns {JSX.Element} Rendered item.
 */
export const AccordionItem: React.FC<AccordionItemProps> = ({
  value,
  children,
  className,
}) => {
  const classNames = useContext(AccordionClassNamesContext);
  const radiusValue = useContext(AccordionRadiusContext);
  return (
    <ShadcnAccordionItem
      value={value}
      className={cn(classNames.item, className)}
      style={radiusValue ? { borderRadius: radiusValue } : undefined}
    >
      {children}
    </ShadcnAccordionItem>
  );
};

/**
 * Accordion control (trigger) adapter.
 *
 * @param {AccordionControlProps} props - Control properties.
 * @returns {JSX.Element} Rendered trigger.
 */
export const AccordionControl: React.FC<AccordionControlProps> = ({
  children,
  icon,
  className,
}) => {
  const classNames = useContext(AccordionClassNamesContext);
  return (
    <AccordionTrigger className={cn(classNames.control, className)}>
      <div className={cn("flex items-center gap-2", classNames.label)}>
        {icon}
        <span>{children}</span>
      </div>
    </AccordionTrigger>
  );
};

/**
 * Accordion panel (content) adapter.
 *
 * @param {AccordionPanelProps} props - Panel properties.
 * @returns {JSX.Element} Rendered content.
 */
export const AccordionPanel: React.FC<AccordionPanelProps> = ({
  children,
  className,
}) => {
  const classNames = useContext(AccordionClassNamesContext);
  return <AccordionContent className={cn(classNames.content || classNames.panel, className)}>{children}</AccordionContent>;
};

Accordion.Item = AccordionItem;
Accordion.Control = AccordionControl;
Accordion.Panel = AccordionPanel;

export default Accordion;
